import Icon from 'components/atoms/Icon';
import { appRoutes } from 'constants/navigation';
import {
  ProjectPageProjectFragment,
  useEditProjectArchivingMutation,
} from 'generated/graphql';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ArchiveProjectModal from 'views/Project/components/ArchiveProjectModal/ArchiveProjectModal';
import ProjectStatusDropdown from 'views/Project/components/ProjectStatusDropdown/ProjectStatusDropdown';
import { Button, Tooltip } from '@spotted-zebra-uk/ui-components';

const COMPANY_ARCHIVED_MESSAGE =
  'This role was archived from the company app and cannot be unarchived here.';

interface IProjectBar {
  project?: ProjectPageProjectFragment;
}

const ProjectBar: FC<IProjectBar> = ({ project }) => {
  const navigate = useNavigate();
  const [archiveProject, { loading: isArchiveProjectLoading }] =
    useEditProjectArchivingMutation({
      onCompleted: () => setIsArchiveProjectModalOpen(false),
    });

  const [isArchiveProjectModalOpen, setIsArchiveProjectModalOpen] =
    useState(false);

  const handleOpenArchiveProjectModal = () => {
    setIsArchiveProjectModalOpen(true);
  };

  const handleArchiveProject = () => {
    if (project) {
      archiveProject({
        variables: { id: project.id, state: !project.isArchived },
      });
    }
  };

  // Archived from the admin app (isArchived) or the company app (deletedAt).
  // Only the admin archive can be undone from here.
  const isArchived = Boolean(project?.isArchived || project?.deletedAt);
  const isArchivedFromCompanyApp = Boolean(project?.deletedAt);

  const backToProjectsPage = () => {
    navigate(appRoutes.projects.url());
  };

  return (
    <div className="project-bar">
      <div className="project-bar__left">
        <Button
          onClick={backToProjectsPage}
          size="small"
          variant="text"
          className="project-bar__back-button"
          leftIcon={
            <Icon icon="left_arrow" className="project-bar__back-button-icon" />
          }
        >
          Roles
        </Button>
      </div>
      {project && (
        <div className="project-bar__right">
          {!isArchived && (
            <Button
              onClick={handleOpenArchiveProjectModal}
              size="large"
              variant="negativeSecondary"
              leftIcon={<Icon icon="delete_outline" />}
            >
              Archive
            </Button>
          )}
          <ProjectStatusDropdown
            projectId={project.id}
            projectStatus={project.status}
            projectName={project.name}
            disabled={isArchived}
          />
          {isArchived && !isArchivedFromCompanyApp && (
            <Button
              onClick={handleOpenArchiveProjectModal}
              size="large"
              variant="primary"
              leftIcon={<Icon icon="unarchive" />}
            >
              Unarchive
            </Button>
          )}
          {isArchivedFromCompanyApp && (
            <Tooltip content={COMPANY_ARCHIVED_MESSAGE} disableAriaLabel>
              <Button
                disabled
                ariaLabel={`Unarchive. ${COMPANY_ARCHIVED_MESSAGE}`}
                size="large"
                variant="primary"
                leftIcon={<Icon icon="unarchive" />}
              >
                Unarchive
              </Button>
            </Tooltip>
          )}
          {isArchiveProjectModalOpen && (
            <ArchiveProjectModal
              projectName={project.name}
              currentProjectStatus={project.status}
              isArchived={project.isArchived}
              onSubmit={handleArchiveProject}
              onCancel={() => setIsArchiveProjectModalOpen(false)}
              loading={isArchiveProjectLoading}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectBar;
